import React, { useCallback, useState } from "react";
import { Formik, useFormikContext } from "formik";
import { Box, Button, Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import store, { AppDispatch, RootReducerType, useAppSelector } from "@store/store";
import { toast } from "react-toastify";
import {
    UtilityFormRendererRetail,
    Field,
    sectionRetailSeekClarification,
    sectionPrivate,
    Section,
} from "@modules/stepper/components/AccessFormData";
import {
    ifHillySectionRetailNew,
    ifRuralSectionPrivateCommonNew,
    ifRuralSectionPrivateNoServiceNew,
    ifRuralSectionRetailNew,
    ifUrbanSectionPrivateCommonNew,
    ifUrbanSectionPrivateNoServiceNew,
    ifUrbanSectionRetailNew,
    // Residential Properties
    ifResidentialUrbanSectionPrivateCommonNew,
    ifResidentialUrbanSectionPrivateNoServiceNew,
    ifResidentialRuralSectionPrivateCommonNew,
    ifResidentialRuralSectionPrivateNoServiceNew,
    // Other Properties
    ifOtherUrbanSectionPrivateCommonNew,
    ifOtherUrbanSectionPrivateNoServiceNew,
    ifOtherRuralSectionPrivateCommonNew,
    ifOtherRuralSectionPrivateNoServiceNew,
    ifExistingSlipRoad,
} from "@modules/stepper/components/data";

import { AmenitiesSectionProps } from "@modules/stepper/types";
import { setSiteRoadwaysField } from "@modules/stepper/redux/siteRoadwaysSlice";
import { setActiveStep } from "@modules/stepper/redux/stepperSlice";
import MuiButton from "@components/Button/MuiButton";
import { RemarksModal, RemarksAttachment } from "./RemarksModal";
import { postSeekClarificationResponseThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { setAccessHoldPayload } from "@components/slider/redux/clarificationSlice";

// --- Helper Types ---
type YesNoQuestion = {
    id: number;
    name: string;
    label: string;
};

const getYesNoQuestions = (sections: any[]): YesNoQuestion[] => {
    const yesNo: YesNoQuestion[] = [];
    if (!Array.isArray(sections)) return yesNo;   // ✅ guard

    const traverse = (field: Field) => {
        if (field.type === "radio" && field.id !== undefined) {
            yesNo.push({
                id: Number(field.id),
                name: field.name.toString(),
                label: field.title,
            });
        }
        if (field.nestedFields && Array.isArray(field.nestedFields)) {
            field.nestedFields.forEach(traverse);
        }
    };

    sections.forEach((section) => {
        section?.fields?.forEach(traverse);       // ✅ guard
    });
    return yesNo;
};

// --- Helper to collect all visible fields based on current values ---
const collectVisibleFields = (
    sections: Section[],
    formValues: any
): Field[] => {
    const visibleFields: Field[] = [];

    const isFieldVisible = (field: Field): boolean => {
        if (!field.showIf) return true;
        const conditionField = field.showIf.field.toString();
        const conditionValue = field.showIf.value;
        const currentValue = formValues[conditionField];

        let isVisible = false;
        if (typeof conditionValue === 'number' && typeof currentValue === 'string') {
            isVisible = Number(currentValue) === conditionValue;
        } else if (typeof conditionValue === 'string' && typeof currentValue === 'number') {
            isVisible = conditionValue === String(currentValue);
        } else {
            isVisible = currentValue === conditionValue;
        }

        return isVisible;
    };

    const traverseFields = (fields: Field[], depth = 0) => {
        fields.forEach((field) => {
            if (isFieldVisible(field)) {
                visibleFields.push(field);

                if (field.nestedFields && field.nestedFields.length > 0) {
                    const fieldValue = formValues[field.name];
                    const shouldShowNested = field.alwaysShowNested ||
                        fieldValue === "Yes" ||
                        (fieldValue !== "" && fieldValue !== undefined && field.type !== "radio");

                    if (shouldShowNested) {
                        traverseFields(field.nestedFields, depth + 1);
                    }
                }
            }
        });
    };

    sections.forEach((section) => {
        if (section.fields) {
            traverseFields(section.fields);
        }
    });

    return visibleFields;
};

// --- Helper function to create dynamic validation schema ---
const createDynamicValidationSchema = (
    dynamicSections: Section[],
    category_id: number,
    formValues: any
) => {
    const shape: Record<string, any> = {};

    const visibleFields = collectVisibleFields(dynamicSections, formValues);

    visibleFields.forEach((field) => {
        const fieldName = field.name.toString();

        if (field.type === "text" || field.type === "date") {
            if (field.dataType === "number") {
                shape[fieldName] = Yup.number()
                    .transform((value, originalValue) => {
                        return originalValue === '' ? undefined : value;
                    })
                    .typeError("Must be a number")
                    .test(
                        'positive-or-zero',
                        'Must be positive',
                        value => value == null || value === 0 || value > 0
                    )
                    .required("Required");
            } else {
                shape[fieldName] = Yup.string().required("Required");
            }
        } else if (field.type === "radio") {
            shape[fieldName] = Yup.string()
                .oneOf(["Yes", "No"], "Required")
                .required("Required");
        } else if (field.type === "select") {
            shape[fieldName] = Yup.string().required("Required");
        }
    });

    shape.location_type_id = Yup.string().required("Required");

    shape.plot_area = Yup.number()
        .typeError("Must be a number")
        .test(
            'positive-or-zero',
            'Must be positive',
            value => value == null || value === 0 || value > 0
        )
        .required("Required");

    if (category_id === 1) {
        shape.is_coco_fuel_station = Yup.string()
            .oneOf(["Yes", "No"], "Required")
            .required("Required");

        shape.loi_name = Yup.string().when("is_coco_fuel_station", {
            is: "No",
            then: (schema) => schema.required("Required"),
            otherwise: (schema) => schema.notRequired(),
        });

        shape.loi_dealer_name = Yup.string().when("is_coco_fuel_station", {
            is: "No",
            then: (schema) => schema.required("Required"),
            otherwise: (schema) => schema.notRequired(),
        });

        shape.loi_number = Yup.string().when("is_coco_fuel_station", {
            is: "No",
            then: (schema) => schema.required("Required"),
            otherwise: (schema) => schema.notRequired(),
        });

        shape.loi_date = Yup.string().when("is_coco_fuel_station", {
            is: "No",
            then: (schema) => schema.required("Required"),
            otherwise: (schema) => schema.notRequired(),
        });
    }

    return Yup.object().shape(shape);
};

// --- Helper: collect all fields (including nested) from any list of sections ---
const collectAllFields = (sections: any[]): Field[] => {
    const all: Field[] = [];
    const traverse = (field: Field) => {
        all.push(field);
        field.nestedFields?.forEach(traverse);
    };
    sections.forEach((section) => section.fields?.forEach(traverse));
    return all;
};


// --- Main Component ---
export const AccessQuestionMain: React.FC<AmenitiesSectionProps & {
    originalLocationTypeId: string | number | null;
}> = ({
    collapsed,
    restrictedFields = [],
    originalLocationTypeId,
}) => {
    const clarificationData = useAppSelector((state: any) => state.clarification.clarificationData,);
    const siteRoadways = useSelector((state: RootReducerType) => state.siteRoadways);

    const category_id = siteRoadways?.category_id;
    const access_id = clarificationData?.application_id;
    const property_type_id = siteRoadways?.property_type_id;

    const QuestionsUpdateCheck = clarificationData?.questions_to_updated;
    const documentRequestNullCheck = clarificationData?.documents_to_updated?.length > 0;

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const accessParam = searchParams.get("access");

    // Remarks modal state
    const [remarksModalOpen, setRemarksModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingFormValues, setPendingFormValues] = useState<any>(null);

    // Clarification flag - adjust path as needed
    const clarificationBothCase = (QuestionsUpdateCheck && documentRequestNullCheck);

    console.log("clarificationBothCase:", clarificationBothCase);

    const effectiveRestrictedFields = QuestionsUpdateCheck ? restrictedFields : [];
    const baseSections = category_id === 2 ? sectionPrivate : sectionRetailSeekClarification;

    const effectivePropertyTypeId =
        property_type_id ??
        (siteRoadways as any)?.property_type_id ??
        "";

    const getDynamicSections = useCallback(
        (locationType: number, serviceRoadType: number, formValues: any = {}) => {
            const dynamicSections = JSON.parse(JSON.stringify(baseSections));
            const normalizedLocationType = Number(locationType);

            const allFieldsFilled =
                formValues.location_type_id &&
                formValues.plot_area;

            const isServiceRoadProvided = formValues.is_service_road_provided;
            const isServiceListVisible = formValues.service_road_type_id;

            const normalizedServiceListVisible = typeof isServiceListVisible === 'string'
                ? Number(isServiceListVisible)
                : isServiceListVisible;

            const shouldShowAccessCriteriaMet = category_id === 2
                ? (allFieldsFilled && (normalizedServiceListVisible === 1 || normalizedServiceListVisible === 2 || normalizedServiceListVisible === 3))
                : ((allFieldsFilled && isServiceRoadProvided === "No") ||
                    (allFieldsFilled && (normalizedServiceListVisible === 1 || normalizedServiceListVisible === 2 || normalizedServiceListVisible === 3)));

            if (!shouldShowAccessCriteriaMet) {
                return [...dynamicSections.slice(0, 1)];
            }

            let locationSpecificSections: Section[] = [];

            const normalizedServiceRoadType = typeof serviceRoadType === 'string'
                ? Number(serviceRoadType)
                : serviceRoadType;

            // 🔥 FIX: prefer formValues.property_type_id if present, then effectivePropertyTypeId
            const rawPropertyTypeId =
                formValues?.property_type_id !== undefined && formValues?.property_type_id !== ""
                    ? formValues.property_type_id
                    : effectivePropertyTypeId;

            const normalizedPropertyTypeId =
                typeof rawPropertyTypeId === 'string'
                    ? Number(rawPropertyTypeId)
                    : rawPropertyTypeId;

            if (category_id === 2) {
                const isResidentialProperty = [7, 8, 9].includes(normalizedPropertyTypeId as any);
                const isOtherProperty = [10, 11, 12, 13, 14].includes(normalizedPropertyTypeId as any);

                if (isResidentialProperty) {
                    switch (normalizedLocationType) {
                        case 1:
                            if (normalizedServiceRoadType === 1) {
                                locationSpecificSections = [];
                            } else if (normalizedServiceRoadType === 2) {
                                locationSpecificSections = ifResidentialUrbanSectionPrivateCommonNew;
                            } else if (normalizedServiceRoadType === 3) {
                                locationSpecificSections = ifResidentialUrbanSectionPrivateNoServiceNew;
                            } else {
                                locationSpecificSections = [];
                            }
                            break;
                        case 2:
                            if (normalizedServiceRoadType === 1) {
                                locationSpecificSections = [];
                            } else if (normalizedServiceRoadType === 2) {
                                locationSpecificSections = ifResidentialRuralSectionPrivateCommonNew;
                            } else if (normalizedServiceRoadType === 3) {
                                locationSpecificSections = ifResidentialRuralSectionPrivateNoServiceNew;
                            } else {
                                locationSpecificSections = [];
                            }
                            break;
                        default:
                            locationSpecificSections = [];
                    }
                } else if (isOtherProperty) {
                    switch (normalizedLocationType) {
                        case 1:
                            if (normalizedServiceRoadType === 1) {
                                locationSpecificSections = [];
                            } else if (normalizedServiceRoadType === 2) {
                                locationSpecificSections = ifOtherUrbanSectionPrivateCommonNew;
                            } else if (normalizedServiceRoadType === 3) {
                                locationSpecificSections = ifOtherUrbanSectionPrivateNoServiceNew;
                            } else {
                                locationSpecificSections = [];
                            }
                            break;
                        case 2:
                            if (normalizedServiceRoadType === 1) {
                                locationSpecificSections = [];
                            } else if (normalizedServiceRoadType === 2) {
                                locationSpecificSections = ifOtherRuralSectionPrivateCommonNew;
                            } else if (normalizedServiceRoadType === 3) {
                                locationSpecificSections = ifOtherRuralSectionPrivateNoServiceNew;
                            } else {
                                locationSpecificSections = [];
                            }
                            break;
                        default:
                            locationSpecificSections = [];
                    }
                } else {
                    locationSpecificSections = [];
                }
            } else {
                switch (normalizedLocationType) {
                    case 1:
                        locationSpecificSections = ifUrbanSectionRetailNew;
                        break;
                    case 2:
                        locationSpecificSections = ifRuralSectionRetailNew;
                        break;
                    case 3:
                        locationSpecificSections = ifHillySectionRetailNew;
                        break;
                    default:
                        locationSpecificSections = [];
                }
            }

            const finalSections = [
                ...dynamicSections.slice(0, 1),
                ...locationSpecificSections,
            ];
            return finalSections;
        },
        [category_id, baseSections, effectivePropertyTypeId]
    );

    const initialLocationType = siteRoadways?.location_type_id;
    const initialServiceRoadType = siteRoadways?.service_road_type_id;
    const initialFormValues = {
        location_type_id: initialLocationType,
        service_road_type_id: initialServiceRoadType,
        property_type_id: effectivePropertyTypeId,
        plot_area: siteRoadways?.plot_area,
        is_service_road_provided: siteRoadways?.is_service_road_provided,
    };
    const initialDynamicSections = getDynamicSections(
        Number(initialLocationType),
        Number(initialServiceRoadType),
        initialFormValues
    );
    const initialYesNoQuestions = getYesNoQuestions(initialDynamicSections);

    const question_true = siteRoadways?.question_true ?? [];
    const question_false = siteRoadways?.question_false ?? [];

    const allPossibleSections = [
        ...sectionRetailSeekClarification,
        ...sectionPrivate,
        ...ifUrbanSectionRetailNew,
        ...ifRuralSectionRetailNew,
        ...ifHillySectionRetailNew,
        ...ifUrbanSectionPrivateCommonNew,
        ...ifUrbanSectionPrivateNoServiceNew,
        ...ifRuralSectionPrivateCommonNew,
        ...ifRuralSectionPrivateNoServiceNew,
        ...ifResidentialUrbanSectionPrivateCommonNew,
        ...ifResidentialUrbanSectionPrivateNoServiceNew,
        ...ifResidentialRuralSectionPrivateCommonNew,
        ...ifResidentialRuralSectionPrivateNoServiceNew,
        ...ifOtherUrbanSectionPrivateCommonNew,
        ...ifOtherUrbanSectionPrivateNoServiceNew,
        ...ifOtherRuralSectionPrivateCommonNew,
        ...ifOtherRuralSectionPrivateNoServiceNew,
        ...ifExistingSlipRoad,
    ];

    const everyPossibleField = collectAllFields(allPossibleSections);

    const ALL_QUESTION_FIELDS = getYesNoQuestions(allPossibleSections);

    const ALL_QUESTION_ID_SET = new Set<number>(
        ALL_QUESTION_FIELDS.map((q) => q.id)
    );

    const ALL_QUESTION_NAME_SET = new Set<string>(
        ALL_QUESTION_FIELDS.map((q) => String(q.name))
    );

    const META_KEYS = new Set<string>([
        "questions",
        "question_true",
        "question_false",
        "questionList",
        "selectedQuestions",
        "questions_to_updated",
    ]);

    // ✅ Whitelist of every real form field name (across every section variant)
    const ALLOWED_FIELD_NAMES = new Set<string>(
        everyPossibleField.map((f) => String(f.name))
    );

    const initialValues: Record<string, any> = {};

    // 1️⃣ Seed every possible field from Redux siteRoadways
    everyPossibleField.forEach((field) => {
        const key = String(field.name);
        const reduxVal = (siteRoadways as any)?.[key];
        if (reduxVal !== undefined && reduxVal !== null) {
            initialValues[key] = reduxVal;
        }
    });

    // 2️⃣ Only copy specific known keys from siteRoadways (whitelist)
    const EXTRA_ALLOWED_KEYS = [
        "property_type_id",
        "location_type_id",
        "service_road_type_id",
        "is_service_road_provided",
        "is_coco_fuel_station",
        "plot_area",
        "plot_length_m",
        "plot_width_m",
    ];

    EXTRA_ALLOWED_KEYS.forEach((key) => {
        const v = (siteRoadways as any)?.[key];
        if (v !== undefined && v !== null && initialValues[key] === undefined) {
            initialValues[key] = v;
        }
    });

    // 3️⃣ Apply penalty consent booleans (cast to any to avoid TS errors)
    const siteRoadwaysAny = siteRoadways as any;
    if (category_id !== 2 && (siteRoadwaysAny?.is_service_road_provided as any) === "No") {
        initialValues.is_constructed_without_prior_permission_consent =
            siteRoadwaysAny?.is_constructed_without_prior_permission_consent ?? false;
        initialValues.is_facility_operational_consent =
            siteRoadwaysAny?.is_facility_operational_consent ?? false;
    }

    // 4️⃣ Apply question_true / question_false → Yes/No mapping
    initialYesNoQuestions.forEach((q) => {
        const key = q.name;
        if (question_true.includes(q.id)) initialValues[key] = "Yes";
        else if (question_false.includes(q.id)) initialValues[key] = "No";
        else if (initialValues[key] === undefined) initialValues[key] = "";
    });

    // 5️⃣ Ensure every field has at least "" so Formik controls them
    everyPossibleField.forEach((field) => {
        const key = String(field.name);
        if (initialValues[key] === undefined) initialValues[key] = "";
    });

    const backButtonClickHandler = () => {
        navigate(-1);
    };

    // ============ Build the payload (extracted for reuse) ============
    const buildPayload = useCallback(
        (
            values: any,
            remarks?: string,
            attachments?: RemarksAttachment[],
        ) => {
            const question_true: number[] = [];
            const question_false: number[] = [];

            ALL_QUESTION_FIELDS.forEach(({ id, name }) => {
                const answer = values[name];
                if (answer === "Yes") question_true.push(id);
                else if (answer === "No") question_false.push(id);
            });

            const inputPayload: Record<string, any> = {};

            Object.keys(values || {}).forEach((key) => {
                if (!ALLOWED_FIELD_NAMES.has(key) && !META_KEYS.has(key)) return;
                if (ALL_QUESTION_NAME_SET.has(key)) return;
                if (/^\d+$/.test(key) && ALL_QUESTION_ID_SET.has(Number(key))) return;
                if (META_KEYS.has(key)) return;

                const raw = values[key];
                if (raw === "" || raw === undefined || raw === null) return;

                if (raw === "Yes") inputPayload[key] = true;
                else if (raw === "No") inputPayload[key] = false;
                else inputPayload[key] = raw;
            });

            const finalPayload: Record<string, any> = {
                application_id: access_id,
                recipient_id: clarificationData?.sender_details?.sender_id,
                recipient_role: clarificationData?.sender_role,

                step2_details: {
                    question_true,
                    question_false,
                    ...inputPayload,
                },

                // ✅ remarks
                ...(remarks ? { message: remarks } : {}),

                // ✅ attachments → clk_documents
                ...(attachments && attachments.length > 0
                    ? {
                        clk_documents: attachments.map((file) => ({
                            fileName: file.fileName,
                            originalFileName: file.originalFileName,
                            objectKey: file.objectKey,
                            mimeType: file.mimeType || "pdf",
                        })),
                    }
                    : {}),
            };

            return finalPayload;
        },
        [category_id, access_id, clarificationData],
    );

    // ============ Navigation helper ============
    const navigateToUpdateDocuments = useCallback(() => {
        navigate(`/update-documents?access=${clarificationData?.application_id}`);
    }, [navigate, clarificationData?.application_id]);

    // ============ Core submit logic (real API call) ============
    const performSubmit = useCallback(
        async (values: any, remarks?: string, attachments?: RemarksAttachment[]) => {
            setIsSubmitting(true);
            try {
                const finalPayload = buildPayload(values, remarks, attachments);
                const response = await dispatch(
                    postSeekClarificationResponseThunk(finalPayload),
                );
                if (response.payload.success) {
                    toast.success("Access Amenities Information saved successfully");
                    if (!clarificationBothCase) navigate("/application-details", { replace: true })
                } else {
                    toast.error(
                        response?.payload?.message || "Failed to save application",
                    );
                }
            } catch (error) {
                toast.error("An error occurred while submitting the form");
            } finally {
                setIsSubmitting(false);
            }
        },
        [dispatch, buildPayload, navigateToUpdateDocuments],
    );

    // ============ Handle submit click ============
    const handleSubmitClick = useCallback(
        (values: any) => {
            const normalizedCurrentLocationType =
                values?.location_type_id === undefined ||
                    values?.location_type_id === null
                    ? ""
                    : String(values.location_type_id);
            const normalizedInitialLocationType =
                originalLocationTypeId === undefined ||
                    originalLocationTypeId === null
                    ? ""
                    : String(originalLocationTypeId);
            const isLocationTypeChanged = normalizedCurrentLocationType !== normalizedInitialLocationType;

            if (isLocationTypeChanged) {
                console.log("Location type changed, stashing payload and navigating to update documents.");
                const stashedPayload = buildPayload(values);
                console.log("Stashed payload:", stashedPayload);
                dispatch(
                    setAccessHoldPayload({
                        step2_details: stashedPayload.step2_details,
                        isLocationTypeChanged: true,
                    }),
                );
                navigateToUpdateDocuments();
                return;
            }

            if (!clarificationBothCase) {
                console.log("Clarification both case is false, opening remarks modal.");
                // ✅ clarification === true → existing flow: remarks modal → real API call
                setPendingFormValues(values);
                setRemarksModalOpen(true);
            } else {
                console.log("Clarification both case is true, stashing payload and navigating to update documents.");
                const stashedPayload = buildPayload(values);
                console.log("Stashed payload:", stashedPayload);
                dispatch(
                    setAccessHoldPayload({
                        step2_details: stashedPayload.step2_details,
                        isLocationTypeChanged: false,
                    }),
                );
                navigateToUpdateDocuments();
            }
        },
        [
            clarificationBothCase,
            buildPayload,
            dispatch,
            navigateToUpdateDocuments,
            originalLocationTypeId,
        ]
    );

    // ============ Remarks modal handlers ============
    const handleRemarksSubmit = useCallback(
        (remarks: string, attachments: RemarksAttachment[]) => {
            setRemarksModalOpen(false);
            if (pendingFormValues) {
                performSubmit(pendingFormValues, remarks, attachments);
                setPendingFormValues(null);
            }
        },
        [pendingFormValues, performSubmit],
    );

    const handleRemarksModalClose = useCallback(() => {
        setRemarksModalOpen(false);
        setPendingFormValues(null);
    }, []);

    return (
        <>
            <Formik
                key={`edit-${clarificationData?.application_id}-${siteRoadways?.location_type_id ?? ""}-${siteRoadways?.service_road_type_id ?? ""}-${effectivePropertyTypeId ?? ""}-${siteRoadways?.is_coco_fuel_station ?? ""}`}
                initialValues={initialValues}
                enableReinitialize={false}
                validate={(values) => {
                    const currentLocationType = values.location_type_id || "";
                    const currentServiceRoadType = values.service_road_type_id || "";
                    const currentDynamicSections = getDynamicSections(
                        currentLocationType,
                        currentServiceRoadType,
                        values
                    );

                    const schema = createDynamicValidationSchema(currentDynamicSections, category_id, values);

                    try {
                        schema.validateSync(values, { abortEarly: false });
                        return {};
                    } catch (err: any) {
                        const errors: Record<string, string> = {};
                        if (err.inner) {
                            err.inner.forEach((error: any) => {
                                if (error.path) {
                                    errors[error.path] = error.message;
                                }
                            });
                        }
                        return errors;
                    }
                }}
                onSubmit={async (values: any) => {
                    handleSubmitClick(values);
                }}>
                {({ handleSubmit, isValid, setFieldValue, values, validateForm, errors, touched }) => {
                    return (
                        <InnerForm
                            category_id={category_id}
                            backButtonClickHandler={backButtonClickHandler}
                            baseSections={baseSections}
                            getDynamicSections={getDynamicSections}
                            handleSubmit={handleSubmit}
                            isValid={isValid}
                            setFieldValue={setFieldValue}
                            values={values}
                            validateForm={validateForm}
                            isSubmitting={isSubmitting}
                            property_type_id={effectivePropertyTypeId}
                            restrictedFields={effectiveRestrictedFields}
                        />
                    );
                }}
            </Formik>

            <RemarksModal
                open={remarksModalOpen}
                onClose={handleRemarksModalClose}
                onSubmit={handleRemarksSubmit}
                loading={isSubmitting}
                accessId={accessParam}
            />
        </>
    );
};

interface InnerFormProps {
    category_id: number;
    backButtonClickHandler: () => void;
    baseSections: any[];
    getDynamicSections: (locationType: any, serviceRoadType: any, formValues?: any) => any[];
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isValid: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    values: any;
    validateForm: (values?: any) => Promise<any>;
    isSubmitting?: boolean;
    property_type_id?: any;
    restrictedFields?: string[];
}

const InnerForm: React.FC<InnerFormProps> = ({
    category_id,
    backButtonClickHandler,
    baseSections,
    getDynamicSections,
    handleSubmit,
    isValid,
    setFieldValue,
    values,
    validateForm,
    isSubmitting,
    property_type_id,
    restrictedFields = [],
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const currentLocationType = values.location_type_id || "";
    const currentServiceRoadType = values.service_road_type_id || "";

    // 🔥 Pass property_type_id into getDynamicSections as part of formValues
    const dynamicSections = getDynamicSections(
        currentLocationType,
        currentServiceRoadType,
        { ...values, property_type_id: property_type_id ?? values.property_type_id }
    );

    const prevSectionsRef = React.useRef<string>("");

    React.useEffect(() => {
        const sectionsString = JSON.stringify(dynamicSections.map((s) => s.title));

        if (sectionsString !== prevSectionsRef.current && prevSectionsRef.current !== "") {
            validateForm();
        }
        prevSectionsRef.current = sectionsString;
    }, [dynamicSections, validateForm, values.service_road_type_id, values.location_type_id]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            validateForm();
        }, 300);
        return () => clearTimeout(timer);
    }, [values, validateForm]);

    return (
        <Box className="amenities-container" component="div" sx={{ height: "calc(100vh - 11rem)", overflowY: "auto" }}>
            <Box className="form-wrapper" sx={{ paddingTop: 0 }}>
                <form onSubmit={handleSubmit}>
                    <UtilityFormRendererRetail
                        sections={dynamicSections}
                        restrictedFields={restrictedFields}
                        handleFieldChange={(name, value) => {
                            if (restrictedFields.includes(String(name))) {
                                return;
                            }
                            setFieldValue(String(name), value, true);
                            dispatch(setSiteRoadwaysField({ name: String(name), value }));

                            if (String(name) === 'is_coco_fuel_station' && value === 'Yes') {
                                setFieldValue('loi_name', '');
                                setFieldValue('loi_dealer_name', '');
                                setFieldValue('loi_number', '');
                                setFieldValue('loi_date', '');
                                dispatch(setSiteRoadwaysField({ name: 'loi_name', value: '' }));
                                dispatch(setSiteRoadwaysField({ name: 'loi_dealer_name', value: '' }));
                                dispatch(setSiteRoadwaysField({ name: 'loi_number', value: '' }));
                                dispatch(setSiteRoadwaysField({ name: 'loi_date', value: '' }));
                            }

                            if (String(name) === 'is_service_road_provided') {
                                if (value === 'No') {
                                    setFieldValue('on_slip_road', '');
                                    setFieldValue('access_criteria_met', '');
                                    dispatch(setSiteRoadwaysField({ name: 'on_slip_road', value: '' }));
                                    dispatch(setSiteRoadwaysField({ name: 'access_criteria_met', value: '' }));
                                }
                            }

                            const allQuestionFields = getYesNoQuestions(dynamicSections);
                            const isQuestionField = allQuestionFields.some(
                                (q) => String(q.name) === String(name),
                            );

                            if (isQuestionField) {
                                const updatedQuestionsTrue: number[] = [];
                                const updatedQuestionsFalse: number[] = [];
                                allQuestionFields.forEach(({ id, name: qName }) => {
                                    const answer = String(name) === String(qName) ? value : values[qName];
                                    if (answer === "Yes") updatedQuestionsTrue.push(id);
                                    else if (answer === "No") updatedQuestionsFalse.push(id);
                                });
                                dispatch(
                                    setSiteRoadwaysField({
                                        name: "question_true",
                                        value: updatedQuestionsTrue,
                                    })
                                );
                                dispatch(
                                    setSiteRoadwaysField({
                                        name: "question_false",
                                        value: updatedQuestionsFalse,
                                    })
                                );
                            }
                        }}
                    />
                </form>
            </Box>
            <Box className="stepper-btns gray:br-t-500 mt-3 py-4 flex gap-4 mr-4" sx={{ justifyContent: "flex-end" }}>
                <MuiButton
                    type="button"
                    name="Back"
                    variant="outlined"
                    fullWidth
                    onClick={backButtonClickHandler}
                    sx={{
                        border: "1px solid #3A1C73",
                        color: `#3A1C73`,
                        borderRadius: "50px",
                        fontSize: "1rem",
                        fontWeight: `var(--font-regular)`,
                        width: "fit-content",
                        height: "unset",
                        transition: "all 0.3s",
                        "&:hover": {
                            background: `#3A1C73`,
                            color: "#fff",
                        },
                    }}
                />
                <MuiButton
                    type="button"
                    name="Submit"
                    variant="outlined"
                    fullWidth
                    disabled={!isValid || isSubmitting}
                    onClick={() => handleSubmit()}
                    sx={{
                        border: "1px solid #3A1C73",
                        color: `#fff !important`,
                        borderRadius: "50px",
                        fontSize: "1rem",
                        fontWeight: `var(--font-regular)`,
                        width: "fit-content",
                        height: "unset",
                        background: `#3A1C73`,
                        transition: "all 0.3s",
                        "&:disabled": {
                            opacity: 0.6,
                            cursor: "not-allowed",
                        },
                    }}
                />
            </Box>
        </Box>
    );
};